import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { PACKS_BASE_PATH } from '../utils/pathsConstants';

import { Discount } from '../types/packTypes';

export const DISCOUNTS_QUERY_KEY = 'discounts';

class DiscountsApi {
    getBySku = async (sku: string): Promise<Discount | null> => {
        const { data } = await axios.get<Discount | null>(
            `/api/${PACKS_BASE_PATH}/discounts/${encodeURIComponent(sku)}`,
        );

        return data;
    };

    save = async ({
        sku,
        payload,
    }: {
        sku: string;
        payload: Pick<Discount, 'discountPrice' | 'startDate' | 'endDate'>;
    }): Promise<Discount> => {
        const { data } = await axios.put<Discount>(
            `/api/${PACKS_BASE_PATH}/discounts/${encodeURIComponent(sku)}`,
            payload,
        );

        return data;
    };

    delete = async (id: number): Promise<void> => {
        await axios.delete(`/api/${PACKS_BASE_PATH}/discounts/${id}`);
    };
}

export const useDiscountsApi = () => {
    return useMemo(() => new DiscountsApi(), []);
};

export const usePackDiscount = (sku: string) => {
    const discountsApi = useDiscountsApi();

    return useQuery({
        queryKey: [DISCOUNTS_QUERY_KEY, sku],
        queryFn: () => discountsApi.getBySku(sku),
        enabled: Boolean(sku),
    });
};

export const useSaveDiscount = () => {
    const discountsApi = useDiscountsApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: discountsApi.save,
        onSuccess: (discount) => {
            queryClient.setQueryData(
                [DISCOUNTS_QUERY_KEY, discount.sku],
                discount,
            );
        },
    });
};

export const useDeleteDiscount = () => {
    const discountsApi = useDiscountsApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: Pick<Discount, 'id' | 'sku'>) =>
            discountsApi.delete(id),
        onSuccess: (_data, { sku }) => {
            queryClient.setQueryData([DISCOUNTS_QUERY_KEY, sku], null);
        },
    });
};
